import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useCardAttributeDeleteMutation } from "src/app/features/CardImplementation/subFeatures/CardDefinition/subFeatures/CardAttribute/services/server/CardAttributeApi";
import type { CardAttributeEntity } from "src/app/features/CardImplementation/subFeatures/CardDefinition/subFeatures/CardAttribute/types/CardAttributeEntity";
import type { CardDefinitionEntity } from "src/app/features/CardImplementation/subFeatures/CardDefinition/types/CardDefinitionEntity";
import { type ExtendedColumnsType } from "../../../../../../../../../components";
import type { AntCustomFormField, CommonFieldAttributes } from "../../../../../../../../../components/Form/types/types";
import { PropertyTable } from "../../../../../../../../../components/Table/components/PropertyTable";
import { displayDateTimeValue } from "../../../../../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { Actions } from "../../../../../../../../../components/Table/types/types";
import { ResourceName } from "../../../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../../../core/router/types/routerTypes";
import {
    showSuccessNotification,
    showWarningNotification,
} from "../../../../../../../../../core/setup/redux/state/notifications/notificationsState";

const getColumnsConfig = (t: Function): ExtendedColumnsType<CardAttributeEntity>[] => {
    return [
        {
            title: t("cardImplementation.attributesTable.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("cardImplementation.attributesTable.extractPattern"),
            key: "extractPattern",
            dataIndex: "extractPattern",
        },
        {
            title: t("cardImplementation.attributesTable.value"),
            key: "value",
            dataIndex: "value",
        },
        {
            title: t("cardImplementation.attributesTable.changed"),
            dataIndex: "changed",
            key: "changed",
            render: (cellValue) => displayDateTimeValue(cellValue),
        },
    ];
};
export interface CardAttributesTableProps extends AntCustomFormField<CardDefinitionEntity[]>, CommonFieldAttributes {}

export const CardAttributesTable: FC<CardAttributesTableProps> = ({ value = [], onChange, readOnly }) => {
    const { id, cardImplementationId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const resourceUrl = `${ResourceName.CARD_IMPLEMENTATIONS}/${cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${id}/${ResourceName.CARD_ATTRIBUTES}`;

    const onDataChanged = (data: CardDefinitionEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onRowClick = (record: CardAttributeEntity) => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    const addCallback = () => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.CREATE}`);
    };

    const [deleteTableItem] = useCardAttributeDeleteMutation();

    const deleteCallback = (item: CardAttributeEntity) => {
        deleteTableItem({
            cardImplementationId,
            cardDefinitionId: id,
            id: item.id,
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("common.formDeleteError", {
                            id: item.id,
                        }),
                    }),
                );
            });

        const performDeleteSuccess = (payload: CardAttributeEntity) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(value.filter((record) => record.id !== item.id));
        };
    };

    const notifyOkDelete = useCallback(
        (payload: CardAttributeEntity) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: payload.id,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    return (
        <PropertyTable<CardAttributeEntity>
            getColumnsConfig={getColumnsConfig}
            name="cardAttributes_table"
            propertyAsRowKey="id"
            tableData={value || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            onRow={(record: CardAttributeEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            resourceUrl={resourceUrl}
            actions={[Actions.ADD, Actions.DELETE, Actions.FORM_COPY]}
            actionCallbacks={{ add: addCallback, delete: (item?: CardAttributeEntity) => item && deleteCallback(item) }}
        />
    );
};
